import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { createWorkflowData, editWorkflowData, usePartnerData, useTypes, useWorkflowRepository } from "@/hooks/useLocalStorage";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AlertDialog } from "@/widgets/AlertDailog";
import { WorkflowAction } from "@/lib/api-services";
import { Code } from "@/shared/schema";


type WorkflowType = "Workflow" | "Subprocess";

type CreateWorkflowValues = {
    WorkflowType: WorkflowType;
    Name: string;
    ExchangePartner?: string;
    ExistingWorkflowId?: string;
    WorkFlowSubCategory: string;
    WorkFlowEntity: string;
    Description: string;
};

type CreateWorkflowProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: any;
    offerData?: any | null;
};

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
            {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>
    );
}

export function CreateWorkflow({ open, onOpenChange, initialData }: CreateWorkflowProps) {
    const isEdit = !!initialData;

    const createMutation = createWorkflowData();
    const updateMutation = editWorkflowData();

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: types, isLoading, refetch } = useWorkflowRepository(
        1,
        undefined,
        undefined,
        undefined,
        undefined
    );

    const { data: entityTypes, isLoading: entityLoading } = useTypes(
        undefined,
        "CUSTENT"
    );

    const { data: subCategoryTypes, isLoading: subCategoryLoading } = useTypes(
        undefined,
        "Workflow User Type"
    );

    const { data: partnerRes, isLoading: partnerLoading } = usePartnerData(1, 100);
    console.log("partnerRes", partnerRes)
    const entityOptions = entityTypes?.filter(
        (item: Code) => item.entityType === "CODE" && item.useFlag === 1
    ) || [];

    const subCategoryOptions = subCategoryTypes?.filter(
        (item: Code) => item.entityType === "CODE" && item.useFlag === 1
    ) || [];

    const partnerOptions = partnerRes?.data?.items || [];

    useEffect(() => {
        refetch();
    }, []);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<CreateWorkflowValues>({
        defaultValues: {
            WorkflowType: "Workflow",
            Name: "",
            ExchangePartner: "",
            ExistingWorkflowId: "",
            WorkFlowSubCategory: "",
            WorkFlowEntity: "",
            Description: "",
        },
    });

    useEffect(() => {
        if (!open) return;

        if (isEdit && initialData) {
            reset({
                WorkflowType: initialData.IsSubprocess ? "Subprocess" : "Workflow",
                Name: initialData.WorkflowName ?? "",
                ExchangePartner: initialData.ExchangePartner?.toString() ?? "",
                ExistingWorkflowId: "",
                WorkFlowSubCategory: initialData.WorkFlowSubCategory?.toString() ?? "",
                WorkFlowEntity: initialData.WorkFlowEntity?.toString() ?? "",
                Description: initialData.Description ?? "",
            });
        } else {
            reset({
                WorkflowType: "Workflow",
                Name: "",
                ExchangePartner: "",
                ExistingWorkflowId: "",
                WorkFlowSubCategory: "",
                WorkFlowEntity: "",
                Description: "",
            });
        }
    }, [open, initialData]);


    const onSubmit = (data: CreateWorkflowValues) => {
        if (isEdit) {
            const editPayload: Record<string, any> = {
                Name: data.Name,
                IsSubprocess: data.WorkflowType === "Subprocess",
            };

            if (data.Description?.trim()) {
                editPayload.Description = data.Description;
            }

            if (data.WorkFlowSubCategory) {
                editPayload.WorkFlowSubCategory = Number(data.WorkFlowSubCategory);
            }

            updateMutation.mutate({ Id: initialData.PMId, data: editPayload as any });
        } else {
            const createPayload: Record<string, any> = {
                Name: data.Name,
                IsSubprocess: data.WorkflowType === "Subprocess",
                Type: "Candidate",
                AddedBy: "Max",
                Status: 0,
                AppID: 1,
            };

            if (data.Description?.trim()) {
                createPayload.Description = data.Description;
            }

            if (data.ExchangePartner) {
                createPayload.ExchangePartner = Number(data.ExchangePartner);
            }

            if (data.WorkFlowSubCategory) {
                createPayload.WorkFlowSubCategory = Number(data.WorkFlowSubCategory);
            }

            if (data.WorkFlowEntity) {
                createPayload.WorkFlowEntity = Number(data.WorkFlowEntity);
            }

            if (data.ExistingWorkflowId) {
                createPayload.ExistingWorkflowId = Number(data.ExistingWorkflowId);
            }

            createMutation.mutate(createPayload as any);
        }
    };

    useEffect(() => {
        if (createMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Workflow Created Successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            createMutation.reset();
        } else if (createMutation.isError) {
            const raw = createMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            createMutation.reset();
        }
    }, [createMutation.isSuccess, createMutation.isError]);

    useEffect(() => {
        if (updateMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Workflow Updated Successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            updateMutation.reset();
        } else if (updateMutation.isError) {
            const raw = updateMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            updateMutation.reset();
        }
    }, [updateMutation.isSuccess, updateMutation.isError]);

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isEdit ? "Edit Workflow" : "Add Workflow"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-1">

                        <Field
                            label="Select Workflow Type"
                            required
                            error={errors.WorkflowType?.message}
                        >
                            <Controller
                                name="WorkflowType"
                                control={control}
                                rules={{ required: "Please select a workflow type" }}
                                render={({ field }) => (
                                    <RadioGroup
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        className="flex flex-row gap-6 pt-1"
                                    >
                                        <div className="flex items-center gap-2">
                                            <RadioGroupItem value="Workflow" id="type-workflow" />
                                            <Label htmlFor="type-workflow" className="font-normal cursor-pointer">
                                                Workflow
                                            </Label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <RadioGroupItem value="Subprocess" id="type-subprocess" />
                                            <Label htmlFor="type-subprocess" className="font-normal cursor-pointer">
                                                Subprocess
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                )}
                            />
                        </Field>

                        <Field
                            label="Workflow Name"
                            required
                            error={errors.Name?.message}
                        >
                            <Input
                                placeholder="Enter workflow name"
                                {...register("Name", { required: "Workflow Name is required" })}
                            />
                        </Field>

                        {!isEdit && (
                            <>
                                <Field
                                    label="Select Exchange Partner"
                                >
                                    <Controller
                                        name="ExchangePartner"
                                        control={control}
                                        render={({ field }) => (
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Exchange Partner" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {partnerLoading ? (
                                                        <div className="p-2 text-sm">Loading...</div>
                                                    ) : (
                                                        partnerOptions.map((p: any) => (
                                                            <SelectItem
                                                                key={p.ID}
                                                                value={p.ID.toString()}
                                                            >
                                                                {p.PartnerName}
                                                            </SelectItem>
                                                        ))
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </Field>

                                <Field label="Use Existing Workflow Design">
                                    <Controller
                                        name="ExistingWorkflowId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                onValueChange={(val) =>
                                                    field.onChange(val === "none" ? "" : val)
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select existing workflow (optional)" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">None</SelectItem>
                                                    {types?.items?.map((wf: WorkflowAction) => (
                                                        <SelectItem key={wf.PMId} value={wf.PMId.toString()}>
                                                            {wf.WorkflowName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </Field>

                                <Field
                                    label="Select Workflow Entity"
                                >
                                    <Controller
                                        name="WorkFlowEntity"
                                        control={control}
                                        render={({ field }) => (
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Entity" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {entityLoading ? (
                                                        <div className="p-2 text-sm">Loading...</div>
                                                    ) : (
                                                        entityOptions.map((e: any) => (
                                                            <SelectItem
                                                                key={e.id}
                                                                value={e.id.toString()}
                                                            >
                                                                {e.name}
                                                            </SelectItem>
                                                        ))
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </Field>
                            </>
                        )}

                        <Field
                            label="Select Workflow Sub Category"
                        >
                            <Controller
                                name="WorkFlowSubCategory"
                                control={control}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Sub Category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {subCategoryLoading ? (
                                                <div className="p-2 text-sm">Loading...</div>
                                            ) : (
                                                subCategoryOptions.map((s: any) => (
                                                    <SelectItem
                                                        key={s.id}
                                                        value={s.id.toString()}
                                                    >
                                                        {s.name}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>

                        <Field label="Description">
                            <Textarea
                                placeholder="Enter description"
                                className="resize-none min-h-[90px]"
                                {...register("Description")}
                            />
                        </Field>

                        <div className="flex justify-end gap-2 pt-1">
                            <Button type="submit" disabled={isPending}>
                                {isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {isEdit ? "Updating..." : "Saving..."}
                                    </>
                                ) : isEdit ? (
                                    "Update"
                                ) : (
                                    "Save"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}